import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './PortalEngajamento.css';
import { 
    FaStar, FaBullhorn, FaHeart, FaSeedling, FaClipboardList, 
    FaSmileBeam, FaSmile, FaMeh, FaFrown, FaTired, 
    FaPaperPlane, FaUserFriends, FaCheckCircle, FaLock, FaQuoteLeft,
    FaRegGrinBeam, FaRegGrin, FaRegMeh, FaRegFrown, FaRegTired,
    FaPlus, FaTrash, FaChevronDown, FaFont, FaStarHalfAlt,
    // (ÍCONES ADICIONADOS)
    FaListAlt, FaSpinner 
} from 'react-icons/fa';

// --- Componente de Spinner (Light Mode) ---
const LoadingSpinner = () => (
    <div className="spinner-overlay-light">
        <div className="spinner-light"></div>
    </div>
);

// (NOVO) Mini Spinner para carregamentos internos
const MiniSpinner = () => <div className="mini-spinner"></div>;

// --- Componente Principal ---
const PortalEngajamento = ({ user }) => {
    const [activeTab, setActiveTab] = useState('visaoGeral');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Dados das abas
    const [visaoGeral, setVisaoGeral] = useState({ humorHoje: null, naoLidos: 0, ultimosElogios: [] });
    const [muralCompleto, setMuralCompleto] = useState([]);
    const [meusFeedbacks, setMeusFeedbacks] = useState({ recebidos: [], enviados: [] });
    const [pesquisas, setPesquisas] = useState([]);
    
    // Lista de funcionários para o modal
    const [funcionarios, setFuncionarios] = useState([]);
    
    // Controle de Modais
    const [isModalOpen, setIsModalOpen] = useState(false); // Modal de Feedback
    const [isPesquisaModalOpen, setIsPesquisaModalOpen] = useState(false); // Modal de Criar Pesquisa
    const [isResponderModalOpen, setIsResponderModalOpen] = useState(false); // Modal de Responder
    const [pesquisaParaResponder, setPesquisaParaResponder] = useState(null); 

    // Verificação de Perfil
    const { perfil, id: myUserId } = user; 
    const adminRoles = ['admin_geral', 'admin', 'rh'];
    const isAdmin = adminRoles.includes(perfil); // (NOVO) Simplificado
    const isModerator = ['admin_geral', 'admin'].includes(perfil);

    // --- Carregamento de Dados ---
    const fetchVisaoGeral = useCallback(async () => {
        setLoading(true);
        try {
            const [visaoRes, funcRes] = await Promise.all([
                api.get('/api/rh/engajamento/visao-geral'),
                api.get('/api/rh/colaboradores') 
            ]);
            setVisaoGeral(visaoRes.data);
            setFuncionarios(funcRes.data.filter(f => f.status === 'ativo' && f.rh_id !== null && f.id !== myUserId));
            setError(null);
        } catch (err) {
            setError("Falha ao carregar o portal. Tente novamente.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [myUserId]);

    const fetchMuralCompleto = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/rh/engajamento/mural-completo');
            setMuralCompleto(res.data);
        } catch (err) {
            setError("Falha ao carregar o mural de elogios.");
        } finally {
            setLoading(false);
        }
    }, []);
    
    const fetchMeusFeedbacks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/rh/engajamento/meus-feedbacks');
            setMeusFeedbacks(res.data);
        } catch (err) {
            setError("Falha ao carregar seus feedbacks.");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPesquisas = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/rh/engajamento/pesquisas');
            setPesquisas(res.data);
        } catch (err) {
            setError("Falha ao carregar pesquisas.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchVisaoGeral();
    }, [fetchVisaoGeral]);

    // --- Handlers ---
    const handleTabClick = (tab) => {
        setActiveTab(tab);
        if (tab === 'mural' && muralCompleto.length === 0) fetchMuralCompleto();
        if (tab === 'meusFeedbacks' && meusFeedbacks.recebidos.length === 0) fetchMeusFeedbacks();
        if (tab === 'pesquisas' && pesquisas.length === 0) fetchPesquisas();
    };

    const handleHumorSubmit = async (sentimento) => {
        const humorAntigo = visaoGeral.humorHoje;
        setVisaoGeral(prev => ({ ...prev, humorHoje: sentimento }));
        if (sentimento === null) return;
        try {
            await api.post('/api/rh/engajamento/humor', { sentimento });
        } catch (err) {
            setError("Erro ao salvar seu humor. Tente novamente.");
            setVisaoGeral(prev => ({ ...prev, humorHoje: humorAntigo }));
        }
    };
    
    const handleFeedbackSent = () => {
        setIsModalOpen(false);
        fetchVisaoGeral();
        setMeusFeedbacks({ recebidos: [], enviados: [] });
        setMuralCompleto([]);
    };

    const handleElogioDelete = async (elogioId) => {
        if (!window.confirm("Tem certeza que deseja excluir este elogio do mural? Esta ação não pode ser desfeita.")) return;
        setLoading(true);
        try {
            await api.delete(`/api/rh/engajamento/feedback/${elogioId}`);
            setVisaoGeral(prev => ({ ...prev, ultimosElogios: prev.ultimosElogios.filter(k => k.id !== elogioId) }));
            setMuralCompleto(prev => prev.filter(k => k.id !== elogioId));
        } catch (err) {
            setError(err.response?.data?.error || "Falha ao excluir elogio.");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    // --- (NOVOS HANDLERS) Para o Modal de Resposta ---
    const handleOpenResponderModal = (pesquisa) => {
        setPesquisaParaResponder(pesquisa);
        setIsResponderModalOpen(true);
    };

    const handleCloseResponderModal = () => {
        setPesquisaParaResponder(null);
        setIsResponderModalOpen(false);
    };

    const handlePesquisaRespondida = () => {
        handleCloseResponderModal();
        fetchPesquisas(); // Recarrega a lista de pesquisas (para mostrar "Respondido")
        fetchVisaoGeral(); // Recarrega a visão geral
    };
    // --- Fim dos novos handlers ---

    return (
        <div className="portal-engajamento-container">
            {loading && <LoadingSpinner />}
            {error && <div className="portal-error-message" onClick={() => setError(null)}>{error}</div>}
            
            <header className="portal-header">
                <h1><FaStar /> Portal de Engajamento</h1>
                <p>Seu espaço central de cultura, feedback e reconhecimento.</p>
            </header>

            <nav className="portal-tabs">
                <button className={`tab-button ${activeTab === 'visaoGeral' ? 'active' : ''}`} onClick={() => handleTabClick('visaoGeral')}>
                    <FaHeart /> Visão Geral
                </button>
                <button className={`tab-button ${activeTab === 'mural' ? 'active' : ''}`} onClick={() => handleTabClick('mural')}>
                    <FaBullhorn /> Mural de Elogios
                </button>
                <button className={`tab-button ${activeTab === 'meusFeedbacks' ? 'active' : ''}`} onClick={() => handleTabClick('meusFeedbacks')}>
                    <FaPaperPlane /> Meus Feedbacks
                    {visaoGeral.naoLidos > 0 && <span className="notification-badge">{visaoGeral.naoLidos}</span>}
                </button>
                <button className={`tab-button ${activeTab === 'pesquisas' ? 'active' : ''}`} onClick={() => handleTabClick('pesquisas')}>
                    <FaClipboardList /> Pesquisas & PDIs
                </button>
            </nav>

            <main className="portal-content">
                {activeTab === 'visaoGeral' && (
                    <VisaoGeral 
                        data={visaoGeral} 
                        onHumorSubmit={handleHumorSubmit} 
                        onOpenModal={() => setIsModalOpen(true)}
                        onTabChange={handleTabClick}
                        isModerator={isModerator}
                        onElogioDelete={handleElogioDelete}
                    />
                )}
                {activeTab === 'mural' && (
                    <MuralDeElogios 
                        elogios={muralCompleto} 
                        isModerator={isModerator}
                        onElogioDelete={handleElogioDelete}
                    />
                )}
                {activeTab === 'meusFeedbacks' && <MeusFeedbacks data={meusFeedbacks} />}
                
                {activeTab === 'pesquisas' && (
                    <PesquisasPDIs 
                        pesquisas={pesquisas} 
                        isAdmin={isAdmin}
                        onAbrirModal={() => setIsPesquisaModalOpen(true)}
                        onAbrirResponderModal={handleOpenResponderModal} 
                        // (PROP ADICIONADA) Passa o usuário logado para o PDI
                        user={user} 
                    />
                )}
            </main>

            {/* --- Modal de Envio de Feedback --- */}
            {isModalOpen && (
                <ModalEnviarFeedback 
                    onClose={() => setIsModalOpen(false)}
                    funcionarios={funcionarios}
                    onFeedbackSent={handleFeedbackSent}
                    setLoading={setLoading}
                    setError={setError}
                />
            )}

            {/* --- Modal de Criação de Pesquisa --- */}
            {isPesquisaModalOpen && (
                <ModalCriarPesquisa
                    onClose={() => setIsPesquisaModalOpen(false)}
                    onPesquisaCriada={() => {
                        setIsPesquisaModalOpen(false);
                        fetchPesquisas(); // Recarrega a lista
                    }}
                    setLoading={setLoading}
                    setError={setError}
                />
            )}

            {/* --- (NOVO) Modal de Resposta de Pesquisa --- */}
            {isResponderModalOpen && pesquisaParaResponder && (
                <ModalResponderPesquisa
                    pesquisa={pesquisaParaResponder}
                    onClose={handleCloseResponderModal}
                    onSuccess={handlePesquisaRespondida}
                    setGlobalLoading={setLoading} // Passa o loading global
                    setGlobalError={setError}
                />
            )}
        </div>
    );
};

// =================================================================
// --- COMPONENTE: Aba 1: Visão Geral ---
// =================================================================
const VisaoGeral = ({ data, onHumorSubmit, onOpenModal, onTabChange, isModerator, onElogioDelete }) => {
    
    const humorIcons = {
        otimo: <FaRegGrinBeam className="humor-icon otimo" onClick={() => onHumorSubmit('otimo')} />,
        bom: <FaRegGrin className="humor-icon bom" onClick={() => onHumorSubmit('bom')} />,
        neutro: <FaRegMeh className="humor-icon neutro" onClick={() => onHumorSubmit('neutro')} />,
        ruim: <FaRegFrown className="humor-icon ruim" onClick={() => onHumorSubmit('ruim')} />,
        pessimo: <FaRegTired className="humor-icon pessimo" onClick={() => onHumorSubmit('pessimo')} />
    };

    return (
        <div className="visao-geral-grid">
            {/* --- Card de Ações Rápidas --- */}
            <div className="portal-card action-card">
                <h3>Ações Rápidas</h3>
                <button className="action-button primary" onClick={onOpenModal}>
                    <FaPaperPlane /> <span>Enviar Feedback ou Elogio</span>
                </button>
                <button className="action-button secondary" onClick={() => onTabChange('mural')}>
                    <FaBullhorn /> <span>Ver Mural de Elogios</span>
                </button>
            </div>

            {/* --- Card Termômetro de Humor --- */}
            <div className="portal-card humor-card">
                <h3>Como você está se sentindo hoje?</h3>
                <div className="humor-icons-wrapper">
                    {data.humorHoje ? (
                        <div className="humor-registrado">
                            {humorIcons[data.humorHoje]}
                            <p>Seu humor de hoje: <strong>{data.humorHoje}</strong></p>
                            <span onClick={() => onHumorSubmit(null)}>(Alterar)</span>
                        </div>
                    ) : (
                        Object.values(humorIcons)
                    )}
                </div>
            </div>

            {/* --- Card Meus Feedbacks --- */}
            <div className="portal-card info-card" onClick={() => onTabChange('meusFeedbacks')}>
                <FaPaperPlane className="info-icon" />
                <div className="info-content">
                    <h4>Meus Feedbacks</h4>
                    {data.naoLidos > 0 ? (
                        <p>Você tem <strong>{data.naoLidos}</strong> novos feedbacks não lidos!</p>
                    ) : (
                        <p>Nenhum feedback novo. Veja seu histórico.</p>
                    )}
                </div>
            </div>

            {/* --- Card Pesquisas Ativas --- */}
            <div className="portal-card info-card" onClick={() => onTabChange('pesquisas')}>
                <FaClipboardList className="info-icon" />
                <div className="info-content">
                    <h4>Pesquisas Ativas</h4>
                    {/* (LÓGICA EXEMPLO) */}
                    <p><strong>Pesquisa de Clima</strong> está aberta!</p>
                </div>
            </div>

            {/* --- Seção Mural de Elogios (Preview) --- */}
            <div className="mural-preview-section">
                <h3>Últimos Elogios do Mural <FaBullhorn /></h3>
                <div className="kudo-preview-grid">
                    {data.ultimosElogios.length > 0 ? (
                        data.ultimosElogios.map(kudo => (
                            <KudoCard 
                                key={kudo.id} 
                                kudo={kudo} 
                                isModerator={isModerator} 
                                onElogioDelete={onElogioDelete} 
                            />
                        ))
                    ) : (
                        <p>O mural de elogios ainda está vazio. Seja o primeiro a elogiar!</p>
                    )}
                </div>
            </div>
            
            {/* --- (ATUALIZADO) Card PDI --- */}
            <div className="portal-card info-card" onClick={() => onTabChange('pesquisas')}>
                <FaSeedling className="info-icon" />
                <div className="info-content">
                    <h4>Meu PDI</h4>
                    <p>Acompanhe seu Plano de Desenvolvimento Individual.</p>
                </div>
            </div>

            {/* --- Placeholder --- */}
            <div className="portal-card placeholder-card">
                <FaUserFriends className="info-icon" />
                <div className="info-content">
                    <h4>Visualizar Time</h4>
                    <p>Navegue pelo organograma da empresa. (Em breve)</p>
                </div>
            </div>
        </div>
    );
};

// =================================================================
// --- COMPONENTE: Aba 2: Mural de Elogios ---
// =================================================================
const MuralDeElogios = ({ elogios, isModerator, onElogioDelete }) => (
    <div className="mural-completo-grid">
        {elogios.length > 0 ? (
            elogios.map(kudo => (
                <KudoCard 
                    key={kudo.id} 
                    kudo={kudo} 
                    isModerator={isModerator} 
                    onElogioDelete={onElogioDelete} 
                />
            ))
        ) : (
            <p>Nenhum elogio público encontrado.</p>
        )}
    </div>
);

// =================================================================
// --- COMPONENTE: Aba 3: Meus Feedbacks ---
// =================================================================
const MeusFeedbacks = ({ data }) => {
    const [subTab, setSubTab] = useState('recebidos');
    return (
        <div className="meus-feedbacks-container">
            <div className="sub-tabs">
                <button 
                    className={`sub-tab-button ${subTab === 'recebidos' ? 'active' : ''}`}
                    onClick={() => setSubTab('recebidos')}
                >
                    Recebidos ({data.recebidos.length})
                </button>
                <button 
                    className={`sub-tab-button ${subTab === 'enviados' ? 'active' : ''}`}
                    onClick={() => setSubTab('enviados')}
                >
                    Enviados ({data.enviados.length})
                </button>
            </div>
            <div className="feedback-list">
                {subTab === 'recebidos' && data.recebidos.map(fb => <FeedbackCard key={fb.id} fb={fb} tipo="recebido" />)}
                {subTab === 'enviados' && data.enviados.map(fb => <FeedbackCard key={fb.id} fb={fb} tipo="enviado" />)}
            </div>
        </div>
    );
};

// =================================================================
// --- (SUPER ATUALIZADO) COMPONENTE: Aba 4: Pesquisas & PDIs ---
// =================================================================
const PesquisasPDIs = ({ pesquisas, isAdmin, onAbrirModal, onAbrirResponderModal, user }) => {
    
    // (NOVO) Lógica para buscar PDIs
    const [meusPDIs, setMeusPDIs] = useState([]);
    const [loadingPDI, setLoadingPDI] = useState(true);
    const [errorPDI, setErrorPDI] = useState(null);

    useEffect(() => {
        const fetchPDIs = async () => {
            // Garante que temos o user.id antes de buscar
            if (!user || !user.id) {
                setLoadingPDI(false);
                return;
            }
            setLoadingPDI(true);
            try {
                // Rota do routesRH.js
                const res = await api.get(`/api/rh/pdi/colaborador/${user.id}`); 
                setMeusPDIs(res.data);
                setErrorPDI(null);
            } catch (err) {
                console.error("Erro ao buscar PDIs:", err);
                setErrorPDI("Falha ao carregar seus Planos de Desenvolvimento.");
            } finally {
                setLoadingPDI(false);
            }
        };
        fetchPDIs();
    }, [user.id]); // Depende do user.id

    return (
        <div className="pesquisas-pdi-container">
            {/* --- Seção de Pesquisas (Existente) --- */}
            <div className="pesquisas-header">
                <h3>Pesquisas de Cultura e Clima</h3>
                {isAdmin && (
                    <button className="button-primary" onClick={onAbrirModal}>
                        <FaPlus /> Criar Nova Pesquisa
                    </button>
                )}
            </div>

            <div className="pesquisas-list">
                {pesquisas.length > 0 ? (
                    pesquisas.map(p => (
                        <div key={p.id} className="pesquisa-card">
                            <div className="pesquisa-card-header">
                                {p.respondido ? (
                                    <span className="status-tag status-respondido">Respondido</span>
                                ) : (
                                    <span className={`status-tag status-${p.status}`}>{p.status}</span>
                                )}
                            </div>
                            <div className="pesquisa-card-body">
                                <h4>{p.titulo}</h4>
                                <p>Tipo: <strong>{p.tipo_pesquisa}</strong></p>
                                <p>Criada por: <strong>{p.criado_por_nome}</strong></p>
                                <p>Encerra em: <strong>{p.data_fim ? new Date(p.data_fim).toLocaleDateString('pt-BR') : 'Indefinido'}</strong></p>
                            </div>
                            <div className="pesquisa-card-footer">
                                <button className="button-secondary" disabled={!isAdmin}>Ver Respostas</button>
                                <button 
                                    className="button-primary"
                                    onClick={() => onAbrirResponderModal(p)}
                                    disabled={p.respondido || p.status !== 'ativa'}
                                >
                                    {p.respondido ? 'Respondido' : 'Responder'}
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="placeholder-container" style={{ margin: 0, padding: '2rem' }}>
                        <FaClipboardList className="placeholder-icon" style={{ marginTop: 0 }} />
                        <p>Nenhuma pesquisa ativa ou concluída encontrada.</p>
                    </div>
                )}
            </div>
            
            {/* --- (ATUALIZADO) Seção do PDI --- */}
            <div className="pdi-container"> 
                <h3><FaSeedling /> Meu PDI (Plano de Desenvolvimento Individual)</h3>
                {loadingPDI ? (
                    <div className="placeholder-container" style={{ margin: 0, padding: '2rem' }}>
                        <MiniSpinner />
                        <p>Buscando seus planos...</p>
                    </div>
                ) : errorPDI ? (
                     <div className="placeholder-container error" style={{ margin: 0, padding: '2rem' }}>
                        <p>{errorPDI}</p>
                    </div>
                ) : meusPDIs.length === 0 ? (
                    <div className="placeholder-container" style={{ margin: 0, padding: '2rem' }}>
                        <FaListAlt className="placeholder-icon" style={{ marginTop: 0 }} />
                        <p>Você ainda não possui um PDI ativo registrado.</p>
                        { !isAdmin && <p>Converse com seu líder para iniciar um.</p> }
                        {/* (Opcional) Botão para RH criar PDI (precisaria de outro modal) */}
                        {/* { isAdmin && (
                            <button className="button-primary" style={{marginTop: '1rem'}}>
                                <FaPlus /> Criar PDI para Colaborador
                            </button>
                        )}
                        */}
                    </div>
                ) : (
                    <div className="pdi-list">
                        {meusPDIs.map(pdi => (
                            <PDICard key={pdi.id} pdi={pdi} isAdmin={isAdmin} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


// =================================================================
// --- (NOVO) COMPONENTE: Card de PDI (com busca de itens) ---
// =================================================================
const PDICard = ({ pdi, isAdmin }) => {
    const [itens, setItens] = useState([]);
    const [isLoadingItens, setIsLoadingItens] = useState(true);
    const [isExpanded, setIsExpanded] = useState(true); // Começa aberto

    useEffect(() => {
        const fetchItens = async () => {
            setIsLoadingItens(true);
            try {
                // (CHAMADA PARA A NOVA ROTA DO BACKEND)
                const res = await api.get(`/api/rh/pdi/${pdi.id}/itens`);
                setItens(res.data);
            } catch (err) {
                console.error("Erro ao buscar itens do PDI", err);
                // Poderia setar um erro aqui
            } finally {
                setIsLoadingItens(false);
            }
        };
        // Só busca os itens se o card estiver expandido (ou na primeira vez)
        if (isExpanded) {
            fetchItens();
        }
    }, [pdi.id, isExpanded]); // Recarrega se expandir

    const getStatusIcon = (status) => {
         if (status === 'concluido') return <FaCheckCircle className="status-icon concluido" />;
         // Vamos tratar 'ativo' como pendente/em andamento
         return <FaSpinner className="status-icon ativo" />; 
    };

    return (
        <div className={`pdi-card ${isExpanded ? 'expanded' : ''}`}>
            <div className="pdi-card-header" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="pdi-header-left">
                    {getStatusIcon(pdi.status)}
                    <h4>{pdi.titulo_pdi}</h4>
                </div>
                <div className="pdi-header-right">
                    <span className="pdi-lider">Líder: {pdi.nome_lider || 'N/A'}</span>
                    <FaChevronDown className={`chevron ${isExpanded ? 'expanded' : ''}`} />
                </div>
            </div>
            {isExpanded && (
                <div className="pdi-card-body">
                    <div className="pdi-info">
                        <span>Início: {new Date(pdi.data_inicio).toLocaleDateString('pt-BR')}</span>
                        <span>Prev. Fim: {new Date(pdi.data_fim_prevista).toLocaleDateString('pt-BR')}</span>
                    </div>
                    <h5>Itens de Desenvolvimento:</h5>
                    {isLoadingItens ? <MiniSpinner /> : (
                        <ul className="pdi-item-list">
                            {itens.length === 0 && <li>Nenhum item de ação cadastrado.</li>}
                            {itens.map(item => (
                                <li key={item.id} className={`pdi-item status-${item.status}`}>
                                    {item.status === 'concluido' ? <FaCheckCircle className="item-icon concluido" /> : <FaListAlt className="item-icon pendente" />}
                                    <div className="item-desc">
                                        <strong>{item.tipo_acao === 'competencia' ? 'Desenvolver Competência' : 'Realizar Treinamento'}</strong>
                                        <p>
                                            {item.descricao_acao}
                                            {item.nome_competencia && ` (Competência: ${item.nome_competencia})`}
                                            {item.nome_curso && ` (Curso: ${item.nome_curso})`}
                                        </p>
                                    </div>
                                    <span className="item-status">{item.status}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {/* (Botão de Adicionar Item - Precisaria de outro modal) */}
                    {/* {isAdmin && <button className="button-secondary button-small"><FaPlus /> Adicionar Item</button>} */}
                </div>
            )}
        </div>
    );
};


// =================================================================
// --- COMPONENTE: Card de Elogio (Kudo) ---
// =================================================================
const KudoCard = ({ kudo, isModerator, onElogioDelete }) => (
    <div className="kudo-card">
        <FaQuoteLeft className="kudo-icon" />
        <p className="kudo-mensagem">"{kudo.mensagem}"</p>
        <div className="kudo-footer">
            <div className="kudo-nomes">
                <strong>De:</strong> {kudo.remetente_nome}<br />
                <strong>Para:</strong> {kudo.destinatario_nome}
            </div>
            <span className="kudo-data">{new Date(kudo.criado_em).toLocaleDateString('pt-BR')}</span>

            {isModerator && (
                <button 
                    className="button-icon-danger kudo-delete-btn" 
                    title="Excluir Elogio"
                    onClick={() => onElogioDelete(kudo.id)}
                >
                    <FaTrash />
                </button>
            )}
        </div>
    </div>
);

// =================================================================
// --- COMPONENTE: Card de Feedback (Privado) ---
// =================================================================
const FeedbackCard = ({ fb, tipo }) => (
    <div className={`feedback-card ${fb.lido === 0 ? 'unread' : ''}`}>
        <div className="feedback-header">
            {tipo === 'recebido' ? (
                <strong>De: {fb.remetente_nome}</strong>
            ) : (
                <strong>Para: {fb.destinatario_nome}</strong>
            )}
            <span className="feedback-data">{new Date(fb.criado_em).toLocaleDateString('pt-BR')}</span>
        </div>
        <p className="feedback-mensagem">{fb.mensagem}</p>
        <div className="feedback-tags">
            <span className={`tag type-${fb.tipo}`}>{fb.tipo === 'elogio' ? 'Elogio' : 'Feedback'}</span>
            <span className={`tag vis-${fb.visibilidade}`}>
                {fb.visibilidade === 'publico' ? <FaBullhorn /> : <FaLock />}
                {fb.visibilidade === 'publico' ? ' Público' : ' Privado'}
            </span>
            {fb.anonimo === 1 && <span className="tag tag-anonimo">Anônimo</span>}
        </div>
    </div>
);

// =================================================================
// --- COMPONENTE: Modal de Envio de Feedback ---
// =================================================================
const ModalEnviarFeedback = ({ onClose, funcionarios, onFeedbackSent, setLoading, setError }) => {
    const [tipo, setTipo] = useState('elogio');
    const [destinatario, setDestinatario] = useState('');
    const [anonimo, setAnonimo] = useState(false);
    const [publico, setPublico] = useState(true);
    const [mensagem, setMensagem] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!destinatario || !mensagem) {
            setError("Por favor, selecione um destinatário e escreva uma mensagem.");
            return;
        }
        
        setLoading(true);
        setError(null);
        
        const payload = {
            destinatario_id: destinatario,
            tipo: tipo,
            visibilidade: (tipo === 'elogio' && publico) ? 'publico' : 'privado',
            anonimo: anonimo,
            mensagem: mensagem
        };

        try {
            await api.post('/api/rh/engajamento/enviar', payload);
            onFeedbackSent();
        } catch (err) {
            setError(err.response?.data?.error || "Falha ao enviar feedback.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Enviar Feedback ou Elogio</h3>
                    <button className="button-close" onClick={onClose}>&times;</button>
                </div>
                <form className="modal-form" onSubmit={handleSubmit}>
                    
                    <div className="form-group">
                        <label>Para quem? *</label>
                        <select value={destinatario} onChange={e => setDestinatario(e.target.value)} required>
                            <option value="">Selecione um colega...</option>
                            {funcionarios.map(f => (
                                <option key={f.id} value={f.id}>{f.nome}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>O que você quer enviar?</label>
                        <div className="tipo-toggle">
                            <button type="button" className={tipo === 'elogio' ? 'active' : ''} onClick={() => setTipo('elogio')}>
                                <FaStar /> Elogio (Kudo)
                            </button>
                            <button type="button" className={tipo === 'feedback' ? 'active' : ''} onClick={() => setTipo('feedback')}>
                                <FaPaperPlane /> Feedback (Privado)
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Sua Mensagem *</label>
                        <textarea 
                            rows="5" 
                            value={mensagem} 
                            onChange={e => setMensagem(e.target.value)}
                            placeholder={tipo === 'elogio' ? 'Escreva seu reconhecimento aqui...' : 'Escreva seu feedback construtivo...'}
                            required
                        ></textarea>
                    </div>
                    
                    <div className="form-options">
                        <label className="checkbox-label">
                            <input type="checkbox" checked={anonimo} onChange={e => setAnonimo(e.target.checked)} />
                            Enviar anonimamente
                        </label>
                        {tipo === 'elogio' && (
                            <label className="checkbox-label">
                                <input type="checkbox" checked={publico} onChange={e => setPublico(e.target.checked)} />
                                Publicar no mural da empresa
                            </label>
                        )}
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="button-secondary" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="button-primary">
                            <FaPaperPlane /> Enviar Agora
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// =================================================================
// --- COMPONENTE: Modal de Criação de Pesquisa ---
// =================================================================
const ModalCriarPesquisa = ({ onClose, onPesquisaCriada, setLoading, setError }) => {
    const [titulo, setTitulo] = useState('');
    const [tipoPesquisa, setTipoPesquisa] = useState('clima');
    const [dataFim, setDataFim] = useState('');
    const [questoes, setQuestoes] = useState([{ id: 1, texto_pergunta: '', tipo_pergunta: 'nota_1_5' }]);
    const [nextId, setNextId] = useState(2);

    const handleAddQuestao = (tipo) => {
        const novaQuestao = { id: nextId, texto_pergunta: '', tipo_pergunta: tipo };
        setQuestoes([...questoes, novaQuestao]);
        setNextId(prev => prev + 1);
    };

    const handleRemoveQuestao = (id) => {
        if (questoes.length <= 1) {
            setError("A pesquisa deve ter pelo menos uma pergunta.");
            setTimeout(() => setError(null), 3000);
            return;
        }
        setQuestoes(questoes.filter(q => q.id !== id));
    };

    const handleQuestaoChange = (id, valor) => {
        setQuestoes(questoes.map(q => q.id === id ? { ...q, texto_pergunta: valor } : q));
    };

    const handleTipoQuestaoChange = (id, tipo) => {
        setQuestoes(questoes.map(q => q.id === id ? { ...q, tipo_pergunta: tipo } : q));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!titulo || questoes.some(q => !q.texto_pergunta)) {
            setError("Por favor, preencha o título e o texto de todas as perguntas.");
            setTimeout(() => setError(null), 3000);
            return;
        }
        
        setLoading(true);
        try {
            const payload = { titulo, tipo_pesquisa: tipoPesquisa, data_fim: dataFim || null, questoes };
            await api.post('/api/rh/engajamento/pesquisas', payload);
            onPesquisaCriada();
        } catch (err) {
            setError(err.response?.data?.error || "Falha ao criar pesquisa.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-xl" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3><FaPlus /> Criar Nova Pesquisa</h3>
                    <button className="button-close" onClick={onClose}>&times;</button>
                </div>
                
                <form className="modal-form pesquisa-form-container" onSubmit={handleSubmit}>
                    <div className="form-row">
                        <div className="form-group" style={{ flex: 2 }}>
                            <label>Título da Pesquisa *</label>
                            <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ex: Pesquisa de Clima Organizacional 2025" required />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Tipo *</label>
                            <select value={tipoPesquisa} onChange={e => setTipoPesquisa(e.target.value)}>
                                <option value="clima">Pesquisa de Clima</option>
                                <option value="cultura">Pesquisa de Cultura</option>
                                <option value="feedback_ciclo">Ciclo de Feedback</option>
                                <option value="outra">Outra</option>
                            </select>
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Data de Encerramento</label>
                            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
                        </div>
                    </div>
                    <hr className="form-divider" />
                    <div className="questoes-list">
                        {questoes.map((q, index) => (
                            <div key={q.id} className="questao-item">
                                <div className="questao-item-header">
                                    <strong>Pergunta {index + 1}</strong>
                                    <div className="questao-tipo-select">
                                        <select value={q.tipo_pergunta} onChange={e => handleTipoQuestaoChange(q.id, e.target.value)}>
                                            <option value="nota_1_5">Termômetro (Nota 1-5)</option>
                                            <option value="texto_livre">Resposta Aberta (Texto)</option>
                                        </select>
                                        <FaChevronDown />
                                    </div>
                                </div>
                                <div className="questao-item-body">
                                    <input type="text" value={q.texto_pergunta} onChange={e => handleQuestaoChange(q.id, e.target.value)} placeholder="Digite o texto da sua pergunta aqui..." required />
                                    <button type="button" className="button-icon-danger" onClick={() => handleRemoveQuestao(q.id)} title="Remover Pergunta">
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="btn-add-questao-wrapper">
                        <button type="button" className="btn-add-questao" onClick={() => handleAddQuestao('nota_1_5')}>
                            <FaStarHalfAlt /> Adicionar Pergunta (Nota)
                        </button>
                        <button type="button" className="btn-add-questao" onClick={() => handleAddQuestao('texto_livre')}>
                            <FaFont /> Adicionar Pergunta (Texto)
                        </button>
                    </div>
                    <div className="modal-footer" style={{ marginTop: '1.5rem' }}>
                        <button type="button" className="button-secondary" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="button-primary">Salvar e Ativar Pesquisa</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// =================================================================
// --- (NOVO) COMPONENTE: Modal de Resposta da Pesquisa ---
// =================================================================
const ModalResponderPesquisa = ({ pesquisa, onClose, onSuccess, setGlobalLoading, setGlobalError }) => {
    const [questoes, setQuestoes] = useState([]);
    const [respostas, setRespostas] = useState({}); // Formato: { questao_id: resposta }
    const [isLoadingModal, setIsLoadingModal] = useState(true);

    // Busca as perguntas da pesquisa ao abrir o modal
    useEffect(() => {
        const fetchQuestoes = async () => {
            setIsLoadingModal(true);
            try {
                const res = await api.get(`/api/rh/engajamento/pesquisa/${pesquisa.id}/responder`);
                setQuestoes(res.data.questoes);
                // Inicializa o estado das respostas
                const respostasIniciais = {};
                res.data.questoes.forEach(q => {
                    respostasIniciais[q.id] = q.tipo_pergunta === 'nota_1_5' ? null : '';
                });
                setRespostas(respostasIniciais);
            } catch (err) {
                setGlobalError(err.response?.data?.error || "Falha ao carregar a pesquisa.");
                onClose(); // Fecha o modal se houver erro (ex: já respondeu)
            } finally {
                setIsLoadingModal(false);
            }
        };
        fetchQuestoes();
    }, [pesquisa.id, onClose, setGlobalError]);

    // Atualiza o estado da resposta
    const handleRespostaChange = (questaoId, resposta) => {
        setRespostas(prev => ({
            ...prev,
            [questaoId]: resposta
        }));
    };

    // Envia as respostas para a API
    const handleSubmit = async (e) => {
        e.preventDefault();
        setGlobalError(null);

        // Validação: verifica se todas as perguntas foram respondidas
        for (const q of questoes) {
            if (respostas[q.id] === null || respostas[q.id] === '') {
                setGlobalError(`A pergunta ${q.ordem} é obrigatória.`);
                setTimeout(() => setGlobalError(null), 3000);
                return;
            }
        }
        
        setGlobalLoading(true); // Usa o spinner global

        // Formata os dados para a API (como a API espera)
        const payload = {
            respostas: Object.keys(respostas).map(questaoId => {
                const questao = questoes.find(q => q.id === parseInt(questaoId));
                return {
                    questao_id: parseInt(questaoId),
                    tipo_pergunta: questao.tipo_pergunta,
                    resposta: respostas[questaoId]
                };
            })
        };
        
        try {
            await api.post(`/api/rh/engajamento/pesquisa/${pesquisa.id}/responder`, payload);
            onSuccess(); // Sucesso (fecha o modal e recarrega a lista)
        } catch (err) {
            setGlobalError(err.response?.data?.error || "Falha ao enviar respostas.");
        } finally {
            setGlobalLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-xl" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{pesquisa.titulo}</h3>
                    <button className="button-close" onClick={onClose}>&times;</button>
                </div>
                
                <form className="modal-form pesquisa-form-container" onSubmit={handleSubmit}>
                    {isLoadingModal ? (
                        <LoadingSpinner /> // Spinner local do modal
                    ) : (
                        <div className="questoes-list">
                            {questoes.map(q => (
                                <div key={q.id} className="questao-responder-item">
                                    <label className="questao-label">
                                        {q.ordem}. {q.texto_pergunta}
                                    </label>
                                    
                                    {q.tipo_pergunta === 'nota_1_5' && (
                                        <TermometroNota 
                                            questaoId={q.id} 
                                            valor={respostas[q.id]}
                                            onChange={handleRespostaChange} 
                                        />
                                    )}

                                    {q.tipo_pergunta === 'texto_livre' && (
                                        <textarea 
                                            rows="4"
                                            value={respostas[q.id]}
                                            onChange={e => handleRespostaChange(q.id, e.target.value)}
                                            placeholder="Digite sua resposta..."
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <div className="modal-footer" style={{ marginTop: '1.5rem' }}>
                        <button type="button" className="button-secondary" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="button-primary" disabled={isLoadingModal}>
                            <FaCheckCircle /> Enviar Respostas
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- (NOVO) Sub-componente para o Termômetro de Nota ---
const TermometroNota = ({ questaoId, valor, onChange }) => {
    const notas = [1, 2, 3, 4, 5];
    return (
        <div className="termometro-wrapper">
            <span className="termometro-label-start">Discordo Totalmente</span>
            <div className="termometro-notas">
                {notas.map(nota => (
                    <label 
                        key={nota} 
                        className={`termometro-nota-bubble ${valor === nota ? 'selected' : ''}`}
                    >
                        <input 
                            type="radio" 
                            name={`q_${questaoId}`} 
                            value={nota}
                            checked={valor === nota}
                            onChange={() => onChange(questaoId, nota)}
                        />
                        {nota}
                    </label>
                ))}
            </div>
            <span className="termometro-label-end">Concordo Totalmente</span>
        </div>
    );
};

export default PortalEngajamento;